import Big from 'big.js'

import { PurchaseFlowProduct } from 'components/pages/PurchaseFlow/_main/contexts/PurchaseFlowProducts.context'
import { PurchaseProductCatalogue } from 'dataQueries/purchase.query'
import { bigFromFee, valueMultiplied } from 'utils/price'

type LinePrices = Pick<PurchaseFlowProduct, 'feePrice' | 'originalPrice'>

/**
 * Props of `embed_pricing_repriced` for the cart as a reprice response prices it: covered (€0 from the free
 * pool), partner-discounted (`originalPrice` > fee > 0) and swapped lines (€0 before this reprice, charged now).
 * The cart's own prices are the previous reprice's; a line the response does not price keeps them, as on screen.
 */
export const embedRepricedProps = (
  cart: Record<number, PurchaseFlowProduct>,
  catalogue: PurchaseProductCatalogue | undefined
) => {
  const repriced = new Map<number, LinePrices>()
  for (const segment of Object.values(catalogue?.segments ?? {})) {
    for (const productType of Object.values(segment?.productTypes ?? {})) {
      for (const product of productType?.products ?? []) repriced.set(product.id, product)
    }
  }

  let coveredCount = 0
  let discountedCount = 0
  let swappedCount = 0
  let chargedTotal = Big(0)

  for (const line of Object.values(cart)) {
    if (!(line.quantity > 0)) continue

    const { feePrice, originalPrice } = repriced.get(line.id) ?? line
    const fee = bigFromFee(feePrice)
    const original = originalPrice ? bigFromFee(originalPrice) : null

    if (fee.eq(0) && original?.gt(0)) coveredCount++
    if (!fee.gt(0)) continue

    chargedTotal = chargedTotal.plus(valueMultiplied(fee, line.quantity))
    if (original?.gt(fee)) discountedCount++
    if (bigFromFee(line.feePrice).eq(0)) swappedCount++
  }

  return {
    coveredCount,
    chargedTotal: chargedTotal.toNumber(),
    partner_discount_shown: discountedCount > 0,
    discountedCount,
    swappedCount,
  }
}
